import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../fleet/FleetManagementView.css";
import "../PremiumGrid.css";
import "./OdometerView.css";
import { Vehicle } from "../../data/database";
import {
    odometerApi,
    OcrAvailability,
    OdometerAlarm,
    OdometerApiError,
    OdometerVehicleStatus,
} from "../../data/odometerApi";
import OdometerEntryModal from "./OdometerEntryModal";
import OdometerScheduleModal from "./OdometerScheduleModal";
import OdometerHistoryPanel from "./OdometerHistoryPanel";

interface OdometerViewProps {
    vehicles: Vehicle[];
}

type CycleFilter = "all" | "scheduled" | "due" | "unset";

const DEFAULT_ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png"];

export default function OdometerView({ vehicles }: OdometerViewProps) {
    const [statuses, setStatuses] = useState<OdometerVehicleStatus[]>([]);
    const [alarms, setAlarms] = useState<OdometerAlarm[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [ocrAvailability, setOcrAvailability] = useState<OcrAvailability>("unknown");
    const [allowedExtensions, setAllowedExtensions] = useState<string[]>(DEFAULT_ALLOWED_EXTENSIONS);

    const [searchQuery, setSearchQuery] = useState("");
    const [cycleFilter, setCycleFilter] = useState<CycleFilter>("all");

    const [entryTarget, setEntryTarget] = useState<OdometerVehicleStatus | null>(null);
    const [scheduleTarget, setScheduleTarget] = useState<OdometerVehicleStatus | null>(null);
    const [historyTarget, setHistoryTarget] = useState<OdometerVehicleStatus | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    const refreshAlarms = useCallback(async () => {
        try {
            const response = await odometerApi.listAlarms();
            setAlarms(response.alarms);
        } catch {
            // 알림은 보조 정보다. 실패해도 본 화면 동작을 막지 않는다.
            setAlarms([]);
        }
    }, []);

    /** 데모 차량 목록을 서버에 등록하고 상태를 받아온다. */
    const bootstrap = useCallback(async () => {
        if (vehicles.length === 0) {
            return;
        }
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await odometerApi.syncVehicles(
                vehicles.map((vehicle) => ({
                    vin: vehicle.vin,
                    vehicleNumber: vehicle.vehicleNumber,
                    model: vehicle.model,
                    depot: vehicle.depot || undefined,
                    // 데모 차량의 현재 누적거리를 최초 1회 기준값으로 넘긴다.
                    baselineKm: vehicle.mileage,
                }))
            );
            setStatuses(response.vehicles);
            await refreshAlarms();
        } catch (error) {
            setLoadError(
                error instanceof OdometerApiError ? error.message : "주행거리 정보를 불러오지 못했습니다."
            );
        } finally {
            setIsLoading(false);
        }
    }, [vehicles, refreshAlarms]);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    useEffect(() => {
        odometerApi
            .ocrStatus()
            .then((status) => {
                setOcrAvailability(status.configured ? "configured" : "unconfigured");
                setAllowedExtensions(status.allowedExtensions);
            })
            // 응답을 못 받았으면 OCR 설정 여부를 알 수 없다. "미설정" 으로 단정하면
            // 실제 원인(API 서버 미기동/프록시 부재)을 가리는 안내가 나간다.
            .catch(() => setOcrAvailability("unknown"));
    }, []);

    const handleSaved = (updated: OdometerVehicleStatus[]) => {
        setStatuses(updated);
        setEntryTarget(null);
        setScheduleTarget(null);
        setRefreshToken((token) => token + 1);
        refreshAlarms();
        // 이력 패널이 열려 있으면 최신 상태로 바꿔 준다.
        setHistoryTarget((current) =>
            current ? updated.find((status) => status.vin === current.vin) || current : current
        );
    };

    const handleConfirmAlarm = async (alarmId: string) => {
        try {
            const response = await odometerApi.confirmAlarm(alarmId);
            setAlarms(response.alarms);
        } catch (error) {
            setLoadError(
                error instanceof OdometerApiError ? error.message : "알림을 확인 처리하지 못했습니다."
            );
        }
    };

    const filteredStatuses = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return statuses.filter((status) => {
            if (query) {
                const haystack =
                    `${status.vehicleNumber} ${status.model || ""} ${status.depot || ""}`.toLowerCase();
                if (!haystack.includes(query)) {
                    return false;
                }
            }
            switch (cycleFilter) {
                case "scheduled":
                    return status.cycle !== "NONE";
                case "due":
                    return status.inputDue;
                case "unset":
                    return status.cycle === "NONE";
                default:
                    return true;
            }
        });
    }, [statuses, searchQuery, cycleFilter]);

    const dueCount = useMemo(() => statuses.filter((status) => status.inputDue).length, [statuses]);
    const scheduledCount = useMemo(
        () => statuses.filter((status) => status.cycle !== "NONE").length,
        [statuses]
    );
    const recordedCount = useMemo(
        () => statuses.filter((status) => status.readingCount > 0).length,
        [statuses]
    );

    return (
        <div className="vm-page">
            <div className="odo-page-header">
                <div className="soh-header-left">
                    <h2 className="soh-header-title">주행거리 관리</h2>
                    <div className="soh-header-sub">
                        차량별 계기판 누적거리를 직접 입력하거나 계기판 사진으로 인식해 등록하고, 입력 주기에 따라
                        리마인드합니다.
                    </div>
                </div>
            </div>

            {/* 리마인드 알림 — 입력 예정일이 도래한 차량 */}
            {alarms.length > 0 && (
                <div className="odo-alarm-stack">
                    {alarms.map((alarm) => (
                        <div className="odo-alarm" key={alarm.alarmId}>
                            <div className="odo-alarm-body">
                                <span className="odo-alarm-badge">{alarm.cycleLabel}</span>
                                <span className="odo-alarm-vehicle">{alarm.vehicleNumber}</span>
                                <span className="odo-alarm-message">{alarm.message}</span>
                                <span className="odo-alarm-date">예정일 {alarm.dueDate}</span>
                            </div>
                            <div className="odo-alarm-actions">
                                <button
                                    type="button"
                                    className="odo-btn primary small"
                                    onClick={() => {
                                        const target = statuses.find((status) => status.vin === alarm.vin);
                                        if (target) {
                                            setEntryTarget(target);
                                        }
                                    }}
                                >
                                    입력하기
                                </button>
                                <button
                                    type="button"
                                    className="odo-btn ghost small"
                                    onClick={() => handleConfirmAlarm(alarm.alarmId)}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* KPI */}
            <div className="odo-kpi-bar">
                <div className="odo-kpi-card">
                    <div className="odo-kpi-label">관리 차량</div>
                    <div className="odo-kpi-value">
                        {statuses.length} <span className="odo-kpi-unit">대</span>
                    </div>
                </div>
                <div className="odo-kpi-card">
                    <div className="odo-kpi-label">입력 필요</div>
                    <div className="odo-kpi-value danger">
                        {dueCount} <span className="odo-kpi-unit">대</span>
                    </div>
                </div>
                <div className="odo-kpi-card">
                    <div className="odo-kpi-label">주기 설정됨</div>
                    <div className="odo-kpi-value">
                        {scheduledCount} <span className="odo-kpi-unit">대</span>
                    </div>
                </div>
                <div className="odo-kpi-card">
                    <div className="odo-kpi-label">입력 이력 보유</div>
                    <div className="odo-kpi-value">
                        {recordedCount} <span className="odo-kpi-unit">대</span>
                    </div>
                </div>
            </div>

            {/* OCR 미설정 안내는 서버가 "설정되지 않았다" 고 실제로 답했을 때만 띄운다.
                응답 자체를 못 받은 unknown 상태에서는 아래 loadError 가 진짜 원인을 알려 준다. */}
            {ocrAvailability === "unconfigured" && (
                <div className="odo-banner warn">
                    서버에 CLOVA OCR이 설정되지 않았습니다. 사진 인식은 비활성 상태이며 직접 입력은 정상
                    동작합니다. (<code>CLOVA_OCR_INVOKE_URL</code>, <code>CLOVA_OCR_SECRET_KEY</code> 환경변수 설정
                    필요)
                </div>
            )}
            {loadError && <div className="odo-banner error">{loadError}</div>}

            {/* 툴바 */}
            <div className="driving-toolbar">
                <div className="driving-toolbar-left">
                    <div className="driving-section-title">차량 목록</div>
                    <div className="driving-tabs">
                        <button
                            className={`driving-tab-btn ${cycleFilter === "all" ? "active" : ""}`}
                            onClick={() => setCycleFilter("all")}
                        >
                            전체
                        </button>
                        <button
                            className={`driving-tab-btn ${cycleFilter === "due" ? "active" : ""}`}
                            onClick={() => setCycleFilter("due")}
                        >
                            입력 필요
                        </button>
                        <button
                            className={`driving-tab-btn ${cycleFilter === "scheduled" ? "active" : ""}`}
                            onClick={() => setCycleFilter("scheduled")}
                        >
                            주기 설정됨
                        </button>
                        <button
                            className={`driving-tab-btn ${cycleFilter === "unset" ? "active" : ""}`}
                            onClick={() => setCycleFilter("unset")}
                        >
                            주기 미설정
                        </button>
                    </div>
                </div>
                <div className="driving-toolbar-right">
                    <div className="driving-search-wrapper">
                        <input
                            type="text"
                            placeholder="차량번호, 차종, 지점 검색"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            className="driving-search-input"
                        />
                        <svg
                            className="driving-search-icon"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </div>
                </div>
            </div>

            {/* 그리드 */}
            <div className="vm-main">
                <div className="soh-table-card">
                    <div className="soh-table-wrapper" style={{ overflowX: "auto" }}>
                        <table className="premium-grid vehicle-grid" style={{ minWidth: "1050px" }}>
                            <thead>
                                <tr>
                                    <th>차량</th>
                                    <th>최근 누적거리</th>
                                    <th>측정일</th>
                                    <th>입력 방식</th>
                                    <th>입력 주기</th>
                                    <th>다음 예정일</th>
                                    <th>상태</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && (
                                    <tr>
                                        <td colSpan={8} className="soh-empty">
                                            주행거리 정보를 불러오는 중입니다...
                                        </td>
                                    </tr>
                                )}
                                {!isLoading &&
                                    filteredStatuses.map((status) => (
                                        <tr
                                            key={status.vin}
                                            className={historyTarget?.vin === status.vin ? "soh-row-selected" : ""}
                                            onClick={() => setHistoryTarget(status)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td className="text-left">
                                                <div className="odo-cell-vehicle">
                                                    <span className="odo-cell-number">
                                                        {status.vehicleNumber}
                                                        {status.depot && (
                                                            <span className="odo-depot-badge">{status.depot}</span>
                                                        )}
                                                    </span>
                                                    <span className="odo-cell-model">{status.model || "-"}</span>
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 600, color: "#0f172a" }}>
                                                {status.latestOdometerKm !== null
                                                    ? `${status.latestOdometerKm.toLocaleString()} km`
                                                    : `${status.baselineKm.toLocaleString()} km`}
                                                {status.latestOdometerKm === null && (
                                                    <span className="odo-baseline-note">등록값</span>
                                                )}
                                            </td>
                                            <td style={{ color: "#475569" }}>{status.latestMeasuredAt || "-"}</td>
                                            <td>
                                                {status.latestInputMethod ? (
                                                    <span
                                                        className={`odo-tag ${
                                                            status.latestInputMethod === "OCR_ASSISTED"
                                                                ? "ocr"
                                                                : "manual"
                                                        }`}
                                                    >
                                                        {status.latestInputMethod === "OCR_ASSISTED"
                                                            ? "사진 인식"
                                                            : "직접 입력"}
                                                    </span>
                                                ) : (
                                                    <span className="odo-muted">-</span>
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className={`grid-badge ${
                                                        status.cycle === "NONE"
                                                            ? "grid-badge-neutral"
                                                            : "odo-badge-cycle"
                                                    }`}
                                                >
                                                    {status.cycleLabel}
                                                </span>
                                            </td>
                                            <td style={{ color: "#475569" }}>{status.nextDueDate || "-"}</td>
                                            <td>
                                                {status.inputDue ? (
                                                    <span className="odo-status-pill due">
                                                        입력 필요
                                                        {status.overdueDays > 0
                                                            ? ` (${status.overdueDays}일 경과)`
                                                            : ""}
                                                    </span>
                                                ) : (
                                                    <span className="odo-status-pill ok">정상</span>
                                                )}
                                            </td>
                                            <td onClick={(event) => event.stopPropagation()}>
                                                <div className="odo-row-actions">
                                                    <button
                                                        type="button"
                                                        className="odo-btn primary small"
                                                        onClick={() => setEntryTarget(status)}
                                                    >
                                                        입력
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="odo-btn ghost small"
                                                        onClick={() => setScheduleTarget(status)}
                                                    >
                                                        주기
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                {!isLoading && filteredStatuses.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="soh-empty">
                                            조건에 맞는 차량이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <OdometerHistoryPanel
                    vehicle={historyTarget}
                    refreshToken={refreshToken}
                    onClose={() => setHistoryTarget(null)}
                />
            </div>

            {entryTarget && (
                <OdometerEntryModal
                    vehicle={entryTarget}
                    ocrAvailability={ocrAvailability}
                    allowedExtensions={allowedExtensions}
                    onClose={() => setEntryTarget(null)}
                    onSaved={handleSaved}
                />
            )}

            {scheduleTarget && (
                <OdometerScheduleModal
                    vehicle={scheduleTarget}
                    onClose={() => setScheduleTarget(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
